/**
 * CycleMyWorkPanel
 *
 * The role-contextual entry surface for a Cycle.
 *
 * When a non-admin user lands on the Cycle Overview tab, this panel resolves
 * their active job(s) within the cycle and renders the appropriate surface.
 *
 * Admin bypass: users with CYCLE_CONFIGURE capability (workspace admins) are
 * never shown this panel — they see the full CycleDetail admin view as usual.
 *
 * A user may hold multiple roles. Each active job is shown in a section,
 * ordered by priority. The primary job is shown first and most prominently.
 *
 * Canon reference: DOCS_V4/00-canon/00-build-contract.v3-planning-included.md §1D
 */

import { ArrowRight, Eye, Lightbulb, Target, Users, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UserCycleRoleContext, CycleJobRole } from '../../lib/cycleRoleContext';
import type { DegapCycle } from '../../types/prioritization/degapCycle';
import type { CatchballLoop } from '../../types/prioritization/catchballLoop';
import type { PrioritizationSession } from '../../types/prioritization/session';
import { ItemProposalFlow } from './ItemProposalFlow';

// ── Role display metadata ─────────────────────────────────────────────────────

const ROLE_META: Record<
  CycleJobRole,
  { label: string; description: string; icon: typeof Target; color: string }
> = {
  cycle_owner: {
    label: 'Cycle Owner',
    description: 'You configure and govern this cycle.',
    icon: Zap,
    color: 'text-[var(--text-ter)]',
  },
  decision_authority: {
    label: 'Decision Authority',
    description: 'The ball is out. Review incoming proposals when they return.',
    icon: Target,
    color: 'text-[var(--text-ter)]',
  },
  ball_holder: {
    label: 'You have the ball',
    description: 'Do the work, then pass it up.',
    icon: Zap,
    color: 'text-[var(--mustard)]',
  },
  facilitator: {
    label: 'Facilitating a session',
    description: 'Open the session, guide participants, and finalize results.',
    icon: Users,
    color: 'text-[var(--text-ter)]',
  },
  session_participant: {
    label: 'Ranking session',
    description: "You've been invited to rank items in a session.",
    icon: Lightbulb,
    color: 'text-[var(--green)]',
  },
  expert_value_provider: {
    label: 'Provide values',
    description: "You've been asked to rate items on specific criteria.",
    icon: Lightbulb,
    color: 'text-[var(--green)]',
  },
  config_contributor: {
    label: 'Configure',
    description: "You've been asked to contribute to session setup.",
    icon: Target,
    color: 'text-[var(--mustard)]',
  },
  sponsor: {
    label: 'Sponsor',
    description: 'Track cycle progress and alignment to strategic objectives.',
    icon: Eye,
    color: 'text-[var(--text-sec)]',
  },
  observer: {
    label: 'Observer',
    description: 'Track cycle progress.',
    icon: Eye,
    color: 'text-[var(--text-ter)]',
  },
  item_proposer: {
    label: 'Suggest items',
    description: 'The item pool is open. Suggest items for consideration.',
    icon: Lightbulb,
    color: 'text-lime-600',
  },
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface CycleMyWorkPanelProps {
  context: UserCycleRoleContext;
  cycle: DegapCycle;
  activeLoop?: CatchballLoop | null;
  sessions: PrioritizationSession[];
}

// ── Job card ──────────────────────────────────────────────────────────────────

function JobCard({
  role,
  cycle,
  activeLoop,
  sessions,
  isPrimary,
}: {
  role: CycleJobRole;
  cycle: DegapCycle;
  activeLoop?: CatchballLoop | null;
  sessions: PrioritizationSession[];
  isPrimary: boolean;
}) {
  const meta = ROLE_META[role];
  const Icon = meta.icon;
  const cycleId = cycle.id;

  const actionLink = getActionLink(role, cycleId, activeLoop, sessions);

  return (
    <div
      className={` border bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-5 ${
        isPrimary
          ? 'border-[var(--slate)] dark:border-[var(--slate)]/60 shadow-sm'
          : 'border-[var(--rule)] dark:border-[var(--rule)]'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`mt-0.5 flex-shrink-0 w-8 h-8  flex items-center justify-center ${
              isPrimary ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30' : 'bg-[var(--surface)] dark:bg-[var(--bg-raised)]/40'
            }`}
          >
            <Icon className={`h-4 w-4 ${meta.color}`} />
          </div>
          <div className="min-w-0">
            <p
              className={`text-sm font-semibold ${
                isPrimary
                  ? 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
                  : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'
              }`}
            >
              {meta.label}
            </p>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
              {meta.description}
            </p>
          </div>
        </div>

        {actionLink && (
          <Link
            to={actionLink.href}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2  transition-colors ${
              isPrimary
                ? 'bg-[var(--slate)] hover:bg-[var(--slate)] text-white'
                : 'border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-pri)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
            }`}
          >
            {actionLink.label}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </div>

      {/* Session list for participant / facilitator / contributor roles */}
      {(role === 'session_participant' ||
        role === 'expert_value_provider' ||
        role === 'config_contributor' ||
        role === 'facilitator') &&
        sessions.length > 0 && (
          <SessionJobList role={role} sessions={sessions} />
        )}

      {/* Item proposal form inline for item proposers */}
      {role === 'item_proposer' && (
        <div className="mt-4 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4">
          <ItemProposalFlow cycle={cycle} showBackLink={false} />
        </div>
      )}
    </div>
  );
}

// ── Session list within a job card ────────────────────────────────────────────

function SessionJobList({
  role: _role,
  sessions,
}: {
  role: CycleJobRole;
  sessions: PrioritizationSession[];
}) {
  const activeSessions = sessions.filter((s) =>
    ['DRAFT', 'CRITERIA_FINALIZED'].includes(s.state),
  );

  if (activeSessions.length === 0) return null;

  return (
    <div className="mt-4 border-t border-[var(--rule)] dark:border-[var(--rule)] pt-4 space-y-2">
      {activeSessions.slice(0, 3).map((s) => {
        const href = `/prioritization/sessions/${s.id}/participate`;

        return (
          <Link
            key={s.id}
            to={href}
            className="flex items-center justify-between gap-3 px-3 py-2.5 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/30 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/20 transition-colors group"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
                {s.name}
              </p>
              {s.trackId && (
                <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
                  {s.state === 'CRITERIA_FINALIZED' ? 'Ready to run' : 'Preparing'}
                </p>
              )}
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-[var(--text-ter)] flex-shrink-0 transition-colors" />
          </Link>
        );
      })}
      {activeSessions.length > 3 && (
        <p className="text-xs text-[var(--text-ter)] pl-3">
          +{activeSessions.length - 3} more session{activeSessions.length - 3 !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

// ── Action link resolver ──────────────────────────────────────────────────────

function getActionLink(
  role: CycleJobRole,
  cycleId: string,
  activeLoop: CatchballLoop | null | undefined,
  sessions: PrioritizationSession[],
): { href: string; label: string } | null {
  switch (role) {
    case 'ball_holder':
      if (activeLoop) {
        return {
          href: `/prioritization/cascade/workbench/${cycleId}/${activeLoop.id}`,
          label: 'Go to workbench',
        };
      }
      return null;

    case 'decision_authority':
      return {
        href: `/prioritization/cycles/${cycleId}?tab=exchange`,
        label: 'View exchange',
      };

    case 'sponsor':
    case 'observer':
      return {
        href: `/prioritization/cycles/${cycleId}?tab=results`,
        label: 'View progress',
      };

    case 'item_proposer':
      return {
        href: `/prioritization/cycles/${cycleId}?tab=tracks`,
        label: 'Suggest items',
      };

    case 'session_participant':
    case 'expert_value_provider':
    case 'config_contributor':
    case 'facilitator': {
      const first = sessions.find((s) =>
        ['DRAFT', 'CRITERIA_FINALIZED'].includes(s.state),
      );
      if (first) {
        // Participants and contributors go to the focused participation view.
        // Facilitators go to the same focused view — they open/run from there.
        return {
          href: `/prioritization/sessions/${first.id}/participate`,
          label: role === 'facilitator' ? 'Open session' : 'Go to session',
        };
      }
      return null;
    }

    default:
      return null;
  }
}

// ── Main panel ────────────────────────────────────────────────────────────────

export function CycleMyWorkPanel({
  context,
  cycle,
  activeLoop,
  sessions,
}: CycleMyWorkPanelProps) {
  const { roles, primaryRole } = context;

  // Filter out cycle_owner from display (admin sees full view, not this panel)
  const displayRoles = roles.filter((r) => r !== 'cycle_owner');

  if (displayRoles.length === 0) {
    return (
      <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-6 py-10 text-center">
        <Eye className="h-6 w-6 text-[var(--text-ter)] mx-auto mb-3" />
        <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          You have read access to this cycle.
        </p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
          No active jobs at the moment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Your work in this cycle
        </h2>
        {displayRoles.length > 1 && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {displayRoles.length} active jobs
          </p>
        )}
      </div>

      {displayRoles.map((role) => (
        <JobCard
          key={role}
          role={role}
          cycle={cycle}
          activeLoop={activeLoop}
          sessions={
            role === 'facilitator' ||
            role === 'session_participant' ||
            role === 'expert_value_provider' ||
            role === 'config_contributor'
              ? sessions
              : []
          }
          isPrimary={role === primaryRole}
        />
      ))}
    </div>
  );
}

export default CycleMyWorkPanel;
